import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch } from 'react-redux';
import { type Node } from 'reactflow';
import { Button, Grid, IconButton, useMediaQuery } from '@mui/material';

import { Icon, ResponsiveDialog, showDeleteDialog, useModel, useSettings, Utils } from 'app/main/components';
import { DetailsSectionWrapper } from 'app/main/components/page-layouts';
import { type WbsNode, type WbsRootNode } from './useWbsState';
import WbsNodeViewerContent from './WbsNodeViewerContent';

interface WbsNodeViewerProps {
	root: WbsRootNode;
	selectedNode: Node<WbsRootNode | WbsNode>;
	setSelectedNode: React.Dispatch<React.SetStateAction<Node<WbsRootNode | WbsNode> | undefined>>;
	updateRefresh: VoidFunction;
}

export default function WbsNodeViewer({ root, selectedNode, setSelectedNode, updateRefresh }: WbsNodeViewerProps) {
	const dispatch = useDispatch();
	const { settings, t } = useSettings('wbs_nodes');
	const isTablet = useMediaQuery(theme => theme.breakpoints.down('md'));

	const { isEditable, isAdd } = selectedNode.data;
	const [isEdit, setIsEdit] = React.useState(isAdd || false);

	const { model, sendChange, save } = useModel({
		baseModel: {
			...(selectedNode.data?.node || {}),
			wbsn_uid: selectedNode.id || uuidv4(),
			wbsn_parent_wbsn_uid: selectedNode.data?.parentId !== 'root' ? selectedNode.data?.parentId : null,
			wbsn_is_leaf: selectedNode.type !== 'branchNode',
			wbsn_table: root.table,
			wbsn_table_uid: root.tableId
		},
		settings,
		isEdit: true,
		isAdd
	});

	function handleClose() {
		setSelectedNode(undefined);
	}

	const title = !isAdd
		? `${model.wbsn_index ? `${model.wbsn_index} - ` : ''}${model.wbsn_title}`
		: t('WBS_NEW_NODE_TITLE');
	const subheader = !isAdd ? model.wbsn_description : selectedNode.data?.parentTitle;

	const content = (
		<WbsNodeViewerContent selectedNode={selectedNode} model={model} isEdit={isEdit} sendChange={sendChange} />
	);

	const actions = isEdit ? (
		<>
			<Button color="secondary" onClick={saveChanges}>
				{t('DIALOG_ACTION_SAVE')}
			</Button>
			{!isAdd && (
				<Button onClick={deleteNode} disabled={!!selectedNode.data?.node?.wbsn_children}>
					{t('VIEW_ITEM_BUTTON_DELETE')}
				</Button>
			)}
			<Button onClick={cancelEdit}>{t('DIALOG_ACTION_CANCEL')}</Button>
		</>
	) : (
		<>
			{isEditable && <Button onClick={() => setIsEdit(true)}>{t('ACTION_EDIT')}</Button>}
			<Button onClick={handleClose}>{t('DIALOG_ACTION_BACK')}</Button>
		</>
	);

	function saveChanges() {
		save({
			onSuccess: () => {
				dispatch(Utils.alertSuccessMsg(t, 'ALERT_ADD_SUCCESS'));
				handleClose();
				updateRefresh();
			}
		});
	}

	function cancelEdit() {
		if (!isAdd) setIsEdit(false);
		else handleClose();
	}

	function deleteNode() {
		dispatch(
			showDeleteDialog({
				module: 'wbs_nodes',
				id: model.wbsn_uid,
				title: model.wbsn_title,
				updateRefresh: () => {
					handleClose();
					updateRefresh();
				},
				hardDelete: true,
				replace: false
			})
		);
	}

	return !isTablet ? (
		<Grid size={{ md: 4 }}>
			<DetailsSectionWrapper
				title={title}
				subheader={subheader}
				headerAction={
					<IconButton>
						<Icon icon="back" />
					</IconButton>
				}
				actions={actions}
			>
				{content}
			</DetailsSectionWrapper>
		</Grid>
	) : (
		<ResponsiveDialog
			open
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
			title={title}
			content={content}
			actions={actions}
		/>
	);
}
